/** @jsx isolateComponent */

import type { ReactNode, MouseEvent as ReactMouseEvent } from 'react';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { Colors } from '@zapier/design-system';

import InAppLinks from './InAppLinks';
import OutOfAppLinks from './OutOfAppLinks';
import {
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../../constants';
import { useQuerySidebar } from '../../hooks';

import type { NavItem } from '../../constants/links';

export type Props = {
  /**
   * Indicates whether the navigation is collapsed down to icons.
   */
  isCollapsed: boolean;
  /**
   * Whether the sidebar is as wide as it can be.
   * When `true`, will render as wide as it can.
   * When `false`, will constrain to a `max-width`.
   */
  isFullWidth?: boolean;
  /**
   * Whether the sidebar has a shadow. Should only be used when
   * the sidebar is floating.
   */
  isShadowed?: boolean;
  /**
   * Array of links to render in the sidebar.
   */
  links?: Array<NavItem>;
  /**
   * Optional handler for when anywhere on the nav is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
  /**
   * Optional handler for when the `nav` is hovered.
   */
  onMouseEnter?: (e: ReactMouseEvent) => void;
  /**
   Optional handler for when the `nav` is unhovered.
   */
  onMouseLeave?: (e: ReactMouseEvent) => void;
  /**
   * Renders content below the links. Typically that content is
   * usage data or CTA buttons.
   */
  renderFooter?: () => ReactNode;
  /**
   * Renders content above the links. Typically that content is
   * a CTA button, such as to create a Zap.
   */
  renderSubheader?: () => ReactNode;
  /**
   * The `href` of the currently selected link within `links`.
   */
  selectedHref: string;
};

const GAP = 20;

const Styles = labelStyles('UniversalSidebar', {
  root: ({
    isCollapsed,
    isFullWidth,
    isShadowed,
  }: Pick<Props, 'isCollapsed' | 'isFullWidth' | 'isShadowed'>) => [
    {
      position: 'relative',
      // Create grid to stretch content so that it scrolls.
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: Colors.neutral100,
      padding: '30px 10px 0 10px',
      // Necessary to set explicitly so `transition` works.
      //width: '100%',
      //transition: Animation.transitionValue,
      // Noticed some jankiness with `transition` and `animation`.
      // This appears to improve it at least a bit.
      willChange: 'transform, width',
    },

    !isFullWidth &&
      withModifier('constrainedWidth', {
        width: UNIVERSAL_SIDEBAR_WIDTH,
        maxWidth: '100vw',
      }),

    !isFullWidth &&
      withModifier('bordered', {
        // Add a border-right w/o the border actually taking
        // up any box model space. border-right reduces the inner
        // width of the content and causes horizontal scrolling
        // which makes this necessary.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 1,
          backgroundColor: Colors.gray20,
        },
      }),

    // Sidebar shadow. Using a pseudo element instead of `box-shadow`
    // so the shadow only goes in one direction.
    isShadowed &&
      withModifier('shadowed', {
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '100%',
          bottom: 0,
          width: 10,
          pointerEvents: 'none',
          background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
        },
      }),

    isCollapsed &&
      withModifier('collapsed', {
        width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
        overflow: 'visible',
      }),
  ],

  header: {
    flex: 'none',
  },

  content: ({ isCollapsed }: Pick<Props, 'isCollapsed'>) => [
    {
      overflow: 'auto',
      paddingTop: GAP,
      paddingBottom: GAP,
      flexBasis: 'auto',
      flexShrink: 1,
      flexGrow: 0,

      // Hide scrollbar, especially on Windows and in Firefox on macOS.
      // Shouldn't be a usability issue since a) scrolling is unlikely
      // to be necessary for most screens, and b) scrolling should still
      // be obvious due to clipped content.
      scrollbarWidth: 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
    isCollapsed &&
      withModifier('collapsed', {
        overflow: 'visible',
      }),
  ],

  links: ({ isCollapsed }: Pick<Props, 'isCollapsed'>) => [
    {
      alignContent: 'start',
      display: 'grid',
      gap: 10,
      listStyle: 'none',
      ...(!isCollapsed && { padding: '0 2px' }),
    },
  ],

  footer: {
    flex: 'none',
    borderTop: `1px solid ${Colors.neutral300}`,
    paddingTop: GAP,
    paddingBottom: GAP,
  },
});

/**
 * @private
 * The universal sidebar that can rener a subeader, links, and a footer.
 * When provided `links` (typically for the out-of-app sidebar), it will render those links.
 * When not provided `links`, it will render the in-app links returned from useSidebarLinkData.
 */
const UniversalSidebar = ({
  isCollapsed = false,
  isFullWidth = true,
  isShadowed,
  links,
  onClick,
  onMouseEnter,
  onMouseLeave,
  renderFooter = () => null,
  renderSubheader,
  selectedHref = '',
}: Props) => {
  const { sideNavData } = useQuerySidebar();

  const mouseProps = {
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
  };

  const subheaderContent = renderSubheader && renderSubheader();
  const subheader = subheaderContent && (
    <div css={Styles.header}>{subheaderContent}</div>
  );

  const footerContent = renderFooter();
  const footer = footerContent && (
    <div css={Styles.footer} role="region">
      {footerContent}
    </div>
  );

  return (
    <nav
      aria-label="Site Links"
      css={Styles.root({ isCollapsed, isFullWidth, isShadowed })}
      data-testid="universalSidebar"
      {...mouseProps}
    >
      {subheader}
      <div css={Styles.content({ isCollapsed })}>
        <ul css={Styles.links({ isCollapsed })}>
          {links ? (
            <OutOfAppLinks isCollapsed={isCollapsed} navigationItems={links} />
          ) : (
            <InAppLinks
              isCollapsed={isCollapsed}
              links={sideNavData}
              selectedHref={selectedHref}
            />
          )}
        </ul>
      </div>
      {footer}
    </nav>
  );
};

export default UniversalSidebar;
